import { useState, type ReactNode } from 'react'

import { Spinner } from '~/components/working'
import { useBeat } from '~/components/use-work'
import { ChevronLeftIcon, ChevronRightIcon } from '~/components/icons'
import { todayKey } from '~/data/weighins'
import {
  CALORIES_PER_GRAM,
  MACROS,
  NUTRIENT_COLORS,
  NUTRIENT_DEEP_COLORS,
  NUTRIENT_LABELS,
  formatCalories,
  formatShare,
  goalOn,
  pastTargetLap,
  type NutritionData,
} from '~/data/nutrition'
import { dayStart, formatDayInMonth, formatMonthYear, monthOfDay } from '~/lib/dates'
import { shifted } from '~/lib/day-step'
import { firstDayOfWeek, weekdayNames } from '~/lib/weekdays'

/** The days offered outright, as steps from today. */
const DAYS_TO_HAND: readonly { label: string; by: number }[] = [
  { label: 'Today', by: 0 },
  { label: 'Yesterday', by: -1 },
  { label: 'Tomorrow', by: 1 },
  { label: 'A week ago', by: -7 },
]

/**
 * The diary's calendar — the nutrition spec, The diary. A month at a time, moved a month at a
 * time, with the days most often wanted offered outright over it: each day of the month wears
 * the two rings the phone's week draws, the outer saying what the day came to against its
 * target and the inner what those calories were made of. Under the month stands the mark that
 * says the open day is not fully logged.
 *
 * Drawn from the diary as it has arrived: the page this opens over waits for the diary before
 * it shows anything, so every ring here is drawn from days that have been read.
 */
export function DayCalendar({
  day,
  data,
  trainedDays,
  incomplete,
  marking,
  withMark = true,
  onPick,
  onIncomplete,
}: {
  /** The day the diary is open on. */
  day: string
  data: NutritionData
  /** The days something was trained on, empty when the diary keeps no workouts. */
  trainedDays: ReadonlySet<string>
  incomplete: boolean
  /** Whether the mark is on its way to the server, during which it cannot be pressed again. */
  marking: boolean
  /**
   * Whether the mark that says the day is not fully logged stands under the month. It is about
   * the day the diary is open on, so a calendar picking a day for something else — the day an
   * entry lands on — carries none.
   */
  withMark?: boolean
  onPick: (day: string) => void
  onIncomplete: (next: boolean) => void
}) {
  const markShown = useBeat(marking)
  const today = todayKey()
  const summaries = new Map(data.days.map((each) => [each.day, each.nutrients]))
  const incompleteDays = new Set(data.incompleteDays)

  return (
    <div className="space-y-3">
      {/* The days most often wanted, reached outright rather than found on the month. */}
      <div role="group" aria-label="Days to hand" className="flex flex-wrap gap-2">
        {DAYS_TO_HAND.map((each) => {
          const at = shifted(today, each.by)
          return (
            <button
              key={each.label}
              type="button"
              aria-label={`Open ${each.label.toLowerCase()}`}
              aria-pressed={at === day}
              className={`rounded-full px-3 py-1 text-sm transition-colors ${
                at === day
                  ? 'bg-hover font-medium text-ink'
                  : 'text-ink-muted hover:bg-hover-faint hover:text-ink'
              }`}
              onClick={() => {
                onPick(at)
              }}
            >
              {each.label}
            </button>
          )
        })}
      </div>

      <MonthGrid day={day}>
        {(key) => {
          const held = summaries.get(key)
          const eaten = held?.calories ?? 0
          const macros = MACROS.map((name) => (held?.[name] ?? 0) * CALORIES_PER_GRAM[name])
          const target = goalOn(data.goals, key)?.calories ?? null
          return (
            <CalendarDay
              day={key}
              number={String(new Date(dayStart(key)).getDate())}
              eaten={eaten}
              target={target}
              macros={macros}
              incomplete={incompleteDays.has(key)}
              trained={trainedDays.has(key)}
              chosen={key === day}
              today={key === today}
              onPick={() => {
                onPick(key)
              }}
            />
          )
        }}
      </MonthGrid>

      {withMark && (
        <label className="flex cursor-pointer items-center gap-2 border-t border-white/10 pt-3 text-sm text-ink-muted">
          {/* The box is what was pressed, so it is the box that says it is working. */}
          <span className="relative grid place-items-center">
            <input
              type="checkbox"
              aria-label="Day incomplete"
              aria-busy={marking || undefined}
              checked={incomplete}
              onChange={(event) => {
                if (!marking) {
                  onIncomplete(event.target.checked)
                }
              }}
              // Faded rather than hidden, so it stays in reach of a screen reader while it works.
              className={`size-4 accent-accent ${markShown ? 'opacity-0' : ''}`}
            />
            {markShown && (
              <span className="pointer-events-none absolute inset-0 grid place-items-center">
                <Spinner className="size-4 text-accent" />
              </span>
            )}
          </span>
          Not everything eaten is logged
        </label>
      )}
    </div>
  )
}

/**
 * A month at a time, moved a month at a time: the month's name between the ways to the months
 * either side, and under it the month in weeks opening on the week's own first day here. What
 * stands on each day is the caller's — the food log's rings, the timeline's plain numbers — so
 * every calendar turns its months the same way.
 */
export function MonthGrid({
  day,
  children,
}: {
  /** The day whose month it opens on. */
  day: string
  /** What stands on one day of the month, given its key. */
  children: (day: string) => ReactNode
}) {
  const [month, setMonth] = useState(monthOfDay(day))
  const [year = 1970, monthNumber = 1] = month.split('-').map(Number)
  const first = new Date(year, monthNumber - 1, 1)
  const daysInMonth = new Date(year, monthNumber, 0).getDate()
  // The column the first of the month falls in, counted from the week's own first day here.
  const isoWeekday = ((first.getDay() + 6) % 7) + 1
  const leading = (isoWeekday - firstDayOfWeek() + 7) % 7
  const cells: (string | null)[] = [
    ...Array.from({ length: leading }, () => null),
    ...Array.from(
      { length: daysInMonth },
      (_, at) => `${month}-${String(at + 1).padStart(2, '0')}`,
    ),
  ]
  while (cells.length % 7 !== 0) {
    cells.push(null)
  }

  const stepMonth = (by: number) => {
    setMonth(monthOfDay(todayKey(new Date(year, monthNumber - 1 + by, 1))))
  }

  return (
    <>
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="The month before"
          className="rounded-full p-1 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          onClick={() => {
            stepMonth(-1)
          }}
        >
          <ChevronLeftIcon aria-hidden className="size-5" />
        </button>
        <h2 className="text-sm font-medium">{formatMonthYear(first.getTime())}</h2>
        <button
          type="button"
          aria-label="The month after"
          className="rounded-full p-1 text-ink-muted transition-colors hover:bg-hover hover:text-ink"
          onClick={() => {
            stepMonth(1)
          }}
        >
          <ChevronRightIcon aria-hidden className="size-5" />
        </button>
      </div>

      <div
        role="grid"
        aria-label={formatMonthYear(first.getTime())}
        className="grid grid-cols-7 gap-y-1"
      >
        <div role="row" className="contents">
          {weekdayNames().map((name) => (
            <div key={name} role="columnheader" className="pb-1 text-center text-xs text-ink-muted">
              {name}
            </div>
          ))}
        </div>
        {Array.from({ length: cells.length / 7 }, (_, row) => (
          <div key={row} role="row" className="contents">
            {cells.slice(row * 7, row * 7 + 7).map((key, column) => {
              if (key === null) {
                return <div key={column} role="gridcell" />
              }
              return (
                <div key={key} role="gridcell" className="flex justify-center">
                  {children(key)}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </>
  )
}

/**
 * One day of the month: its number inside the two rings, and under it the dot that says today
 * and the one that says something was trained. Only the marks a day has earned are laid out,
 * and the row keeps its height whether it holds anything or not, so a month where nothing was
 * trained sits exactly where one where everything was does.
 *
 * The day the diary is open on wears a lit ground — grey rather than the accent, because the
 * ring around it is already a blue and a second blue behind it would be two things saying
 * nothing to each other.
 */
function CalendarDay({
  day,
  number,
  eaten,
  target,
  macros,
  incomplete,
  trained,
  chosen,
  today,
  onPick,
}: {
  day: string
  number: string
  eaten: number
  target: number | null
  /** What the day was made of, in calories, macro by macro. */
  macros: readonly number[]
  incomplete: boolean
  trained: boolean
  chosen: boolean
  today: boolean
  onPick: () => void
}) {
  const over = target !== null && eaten > target
  const whole = macros.reduce((sum, part) => sum + part, 0)
  const said = [
    formatDayInMonth(dayStart(day)),
    ...(eaten > 0 || target !== null
      ? [
          target === null
            ? formatCalories(eaten)
            : `${formatCalories(eaten)} of ${formatCalories(target)}`,
        ]
      : []),
    ...(over ? ['over target'] : []),
    ...(whole > 0
      ? [
          `made of ${MACROS.map(
            (name, at) =>
              `${formatShare((macros[at] ?? 0) / whole)} ${NUTRIENT_LABELS[name].toLowerCase()}`,
          ).join(', ')}`,
        ]
      : []),
    ...(incomplete ? ['incomplete'] : []),
    ...(trained ? ['trained'] : []),
    ...(today ? ['today'] : []),
  ].join(', ')

  return (
    <span className="flex flex-col items-center">
      <button
        type="button"
        aria-label={said}
        aria-pressed={chosen}
        className={`relative grid size-10 place-items-center rounded-full text-sm transition-colors hover:bg-hover ${
          chosen
            ? 'bg-white/20 font-semibold'
            : incomplete
              ? 'bg-danger/10 font-medium'
              : 'font-medium'
        } ${incomplete ? 'text-danger' : 'text-ink'}`}
        onClick={onPick}
      >
        <DayRings eaten={eaten} target={target} macros={macros} />
        <span className="relative">{number}</span>
      </button>
      <span aria-hidden className="mt-0.5 flex h-1.5 items-center gap-1">
        {today && <span className="size-1 rounded-full bg-ink" />}
        {trained && <span className="size-1 rounded-full bg-accent" />}
      </span>
    </span>
  )
}

/** The ring's box, and the strokes inside it — the phone's, to the pixel at one to one. */
const BOX = 40
const OUTER_STROKE = 2.5
const INNER_STROKE = 2
const INNER_GAP = 1.5

/**
 * The two rings a day wears. The outer is the day's calories against that day's own target,
 * drawn from the top; past the target it closes and goes round again in the calories' deeper
 * cut — the same second lap the phone's head draws. Inside it, what those calories were made
 * of: the whole ring is the day's macros however far through the day it is, each taking the
 * share of it that it is of the whole. It is a proportion rather than a progress, so it always
 * closes — what it says is the shape of the day, not how much of it is done.
 */
function DayRings({
  eaten,
  target,
  macros,
}: {
  eaten: number
  target: number | null
  macros: readonly number[]
}) {
  const share = target === null || target <= 0 ? 0 : Math.min(1, eaten / target)
  const spill = pastTargetLap(eaten, target)
  const outerRadius = BOX / 2 - OUTER_STROKE / 2
  const outerAround = 2 * Math.PI * outerRadius
  const innerRadius = BOX / 2 - (OUTER_STROKE + INNER_GAP + INNER_STROKE / 2)
  const innerAround = 2 * Math.PI * innerRadius
  const whole = macros.reduce((sum, part) => sum + part, 0)
  // Each macro's arc starts where the one before it ended.
  const segments = macros.reduce<{ at: number; length: number; offset: number }[]>(
    (laid, part, at) => {
      const length = whole > 0 ? (part / whole) * innerAround : 0
      const offset = laid.reduce((sum, each) => sum + each.length, 0)
      return [...laid, { at, length, offset }]
    },
    [],
  )

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${String(BOX)} ${String(BOX)}`}
      className="absolute inset-0 size-10 -rotate-90"
    >
      <circle
        cx={BOX / 2}
        cy={BOX / 2}
        r={outerRadius}
        fill="none"
        strokeWidth={OUTER_STROKE}
        className="stroke-white/15"
      />
      {share > 0 && (
        <circle
          data-share
          cx={BOX / 2}
          cy={BOX / 2}
          r={outerRadius}
          fill="none"
          strokeWidth={OUTER_STROKE}
          strokeLinecap="round"
          stroke={NUTRIENT_COLORS.calories}
          strokeDasharray={`${String(share * outerAround)} ${String(outerAround)}`}
        />
      )}
      {spill > 0 && (
        <circle
          data-lap
          cx={BOX / 2}
          cy={BOX / 2}
          r={outerRadius}
          fill="none"
          strokeWidth={OUTER_STROKE}
          strokeLinecap="round"
          stroke={NUTRIENT_DEEP_COLORS.calories}
          strokeDasharray={`${String(spill * outerAround)} ${String(outerAround)}`}
        />
      )}
      {segments
        .filter((segment) => segment.length > 0)
        .map((segment) => (
          <circle
            key={segment.at}
            cx={BOX / 2}
            cy={BOX / 2}
            r={innerRadius}
            fill="none"
            strokeWidth={INNER_STROKE}
            stroke={NUTRIENT_COLORS[MACROS[segment.at] ?? 'protein']}
            strokeDasharray={`${String(segment.length)} ${String(innerAround)}`}
            strokeDashoffset={String(-segment.offset)}
          />
        ))}
    </svg>
  )
}
